import { MainContract } from "@/components/views/MultiTenancy/multiTenancyTypes";
import { apiV3Call } from "./apiV3Call";
import {
  AddDevicesToUserParams,
  AddSubcontractCustomerLogoParams,
  AssignDevicesToInviteeParams,
  CreateSubcontractParams,
  DeleteInvitationParams,
  EditInvitationParams,
  EditSubcontractParams,
  EmptyParam,
  EmptyRetval,
  GetAssignableUserRolesRetval,
  GetInvitedUsersRetval,
  GetMultiTenancyOverviewRetval,
  InviteNewUserParams,
  InviteNewUserRetval,
  ResendInvitationParams,
  SetDeviceLocationParams,
  SetUserDevicesParams,
} from "./MTCallTypesAndParams";
import { CreateDashboardWithWidgetsParams, PredefinedMTTemplate } from "@/types";

export class MultiTenancyConnector {
  private readonly urlPrefix: string;

  constructor(urlPrefix: string) {
    this.urlPrefix = urlPrefix;
  }

  /**
   * @target "/api/v3.0/get-multi-tenancy-overview"
   */
  async getMainContract() {
    const result = await apiV3Call<EmptyParam, GetMultiTenancyOverviewRetval>(this.urlPrefix + "/api/v3.0/get-multi-tenancy-overview", {});
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    const { subcontractOverviews, ...rest } = result.data;
    return { ...rest, subcontracts: subcontractOverviews } satisfies MainContract;
  }

  /**
   * @target "/api/v3.0/create-subcontract"
   */
  async createSubcontract(params: CreateSubcontractParams) {
    const result = await apiV3Call<CreateSubcontractParams, { subContractId: string }>(this.urlPrefix + "/api/v3.0/create-subcontract", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data;
  }

  /**
   * @target "/api/v3.0/edit-subcontract"
   */
  async editSubcontract(params: EditSubcontractParams) {
    const result = await apiV3Call<EditSubcontractParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/edit-subcontract", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data;
  }

  /**
   * @target "/api/v3.0/set-subcontract-customer-logo"
   */
  async setSubcontractCustomerLogo(params: AddSubcontractCustomerLogoParams) {
    const result = await apiV3Call<AddSubcontractCustomerLogoParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/set-subcontract-customer-logo", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data;
  }

  /**
   * @target "/api/v3.0/delete-subcontract-customer-logo"
   */
  async deleteSubcontractCustomerLogo(subContractId: string) {
    const result = await apiV3Call<{ subContractId: string }, EmptyRetval>(this.urlPrefix + "/api/v3.0/delete-subcontract-customer-logo", {
      subContractId,
    });
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data;
  }

  /**
   * @target "/api/v3.0/invite-new-user"
   */
  async inviteNewUser(params: InviteNewUserParams) {
    const result = await apiV3Call<InviteNewUserParams, InviteNewUserRetval>(this.urlPrefix + "/api/v3.0/invite-new-user", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data;
  }

  /**
   * @target "/api/v3.0/get-user-invitations"
   */
  async getUserInvitations(contractId: string) {
    const result = await apiV3Call<{ contract: string }, GetInvitedUsersRetval>(this.urlPrefix + "/api/v3.0/get-user-invitations", {
      contract: contractId,
    });
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data.invitations;
  }

  /**
   * @target "/api/v3.0/assign-devices-to-new-invitee"
   */
  async assignDevicesToInvitee(params: AssignDevicesToInviteeParams) {
    const result = await apiV3Call<AssignDevicesToInviteeParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/assign-devices-to-new-invitee", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return {};
  }

  /**
   * @target "/api/v3.0/add-devices-to-user"
   */
  async addDevicesToUser(params: AddDevicesToUserParams) {
    const result = await apiV3Call<AddDevicesToUserParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/add-devices-to-user", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return {};
  }

  /**
   * @target "/api/v3.0/set-user-devices"
   */
  async setUserDevices(params: SetUserDevicesParams) {
    const result = await apiV3Call<SetUserDevicesParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/set-user-devices", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return {};
  }

  /**
   * @target "/api/v3.0/delete-user-invitation"
   */
  async deleteInvitation(token: string) {
    const result = await apiV3Call<DeleteInvitationParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/delete-user-invitation", { token });
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return {};
  }

  /**
   * @target "/api/v3.0/edit-user-invitation"
   */
  async editInvitation(params: EditInvitationParams) {
    const result = await apiV3Call<EditInvitationParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/edit-user-invitation", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return {};
  }

  /**
   * @target "/api/v3.0/resend-user-invitation"
   */
  async resendInvitation(token: string) {
    const result = await apiV3Call<ResendInvitationParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/resend-user-invitation", { token });
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return {};
  }

  /**
   * @target "/api/v3.0/set-device-location"
   */
  async setDeviceLocation(params: SetDeviceLocationParams) {
    const result = await apiV3Call<SetDeviceLocationParams, EmptyRetval>(this.urlPrefix + "/api/v3.0/set-device-location", params);
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return {};
  }

  /**
   * @target "/api/v3.0/get-assignable-subcontract-user-roles"
   */
  async getRolesForInvitees() {
    const result = await apiV3Call<EmptyParam, GetAssignableUserRolesRetval>(this.urlPrefix + "/api/v3.0/get-assignable-subcontract-user-roles", {});
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data.roles;
  }

    /**
   * @target "/api/v3.0/get-predefined-templates"
   */
  async getMTPredefinedTemplates() {
    const result = await apiV3Call<Record<string, never>, PredefinedMTTemplate[]>(
      this.urlPrefix + "/api/v3.0/get-predefined-templates",
      {},
    );
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data;
  }

    /**
   * @target "/api/v3.0/create-mt-dashboard-with-widgets"
   */
  async createMTDashboardWithWidgets(params: CreateDashboardWithWidgetsParams) {
    const result = await apiV3Call<CreateDashboardWithWidgetsParams, Record<string, never>>(
      this.urlPrefix + "/api/v3.0/create-mt-dashboard-with-widgets",
      params,
    );
    if (result.error) {
      throw new Error(result.error.messageKey);
    }
    return result.data;
  }
}
